// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { createFileRoute, useBlocker } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AccessDenied } from "@/components/shared/access-denied";
import { UnsavedChangesDialog } from "@/components/shared/unsaved-changes-dialog";
import { Tabs, TabsContent, TabsIndicator, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChainEditTab, type ChainEditTabHandle } from "@/features/chains/chain-edit-tab";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";

export const Route = createFileRoute("/_authenticated/chains/$chainName/edit")({
  component: ChainEditPage,
});

/* One tab for now — the row matches the store's edit page and leaves room for more. */
function ChainEditPage() {
  const { chainName } = Route.useParams();
  const { hasPermission } = useAuth();
  const { t } = useTranslation("back-office");

  const [isFormDirty, setIsFormDirty] = useState(false);
  const editTabRef = useRef<ChainEditTabHandle>(null);

  const blocker = useBlocker({
    shouldBlockFn: () => isFormDirty,
    enableBeforeUnload: () => isFormDirty,
    withResolver: true,
  });

  if (!hasPermission(PAGE_PERMISSIONS.chainsEdit)) return <AccessDenied permission={PAGE_PERMISSIONS.chainsEdit} />;

  return (
    <div>
      <Tabs value="general" className="gap-0">
        <div className="w-full overflow-x-auto overflow-y-hidden pl-5 pr-6 pt-6">
          <TabsList>
            <TabsIndicator />
            <TabsTrigger value="general">{t("tabs.general")}</TabsTrigger>
          </TabsList>
        </div>
        <TabsContent value="general" className="space-y-4">
          <ChainEditTab ref={editTabRef} chainName={chainName} onDirtyChange={setIsFormDirty} />
        </TabsContent>
      </Tabs>

      <UnsavedChangesDialog
        open={blocker.status === "blocked"}
        onStay={() => blocker.reset?.()}
        onDiscard={() => {
          editTabRef.current?.reset();
          blocker.proceed?.();
        }}
      />
    </div>
  );
}
